// @flow
import React, { useState, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { getS3AssetsManagement } from 'shared/lib/clientSettings';
import Modal from '~/app/components/Modal';
import Button from '~/app/ui/Button';
import isImage from 'shared/lib/isImage';
import FilePicker from './FilePicker';
import FileBrowser from './FileBrowser';
import ImageCropperManager from './ImageCropperManager';

type Props = {
  classes: Object,
  value: ?string,
  onChange: (string) => void,
  options: object
}

// Get S3 path from currently selected asset URL
const getFullAssetPath = originalUrl => (originalUrl && originalUrl.split('/').slice(3).join('/')) || '';

// Get path of the selected asset's folder
const getAssetFolder = assetPath => (assetPath ?  assetPath.substring(0, assetPath.lastIndexOf('/') + 1): '');

const Container = (props: Props) => {
  const croppersRef = useRef();

  const { value: inputValue, onChange, options } = props;
  const cropEnabled = options?.cropEnabled;
  const croppers = options?.croppers;

  // There are 3 data type with value.
  // If cropEnabled is false, value should be a string as old way.
  // If cropEnabled is true, the value can be:
  // - object with urls for different screen size
  // - string for a single image url
  // For example: {
  //  original: 'http://www.ff.com/asset/home-page.jpg',
  //  mobile: 'http://www.ff.com/asset/home-page-mobile.jpg'
  //  desktop: 'http://www.ff.com/asset/home-page-desktop.jpg'
  // }
  const value = inputValue?.original || inputValue;

  const [selectedAsset, setSelectedAsset] = useState<string>(getFullAssetPath(value));
  const [path, setPath] = useState<string>(getAssetFolder(selectedAsset));
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isCropping, setIsCropping] = useState<boolean>(false);

  const c = useStyles();

  // Encode URI to remove url-unfriendly characters
  const selectedAssetUrl = selectedAsset ? `${getS3AssetsManagement() || ''}/${encodeURI(selectedAsset)}` : '';

  const handleOpenCropTool = () => {
    setIsCropping(true);
  }

  const handleBack = () => {
    setIsCropping(false);
  }

  const handleCancel = () => {
    setModalOpen(false);
  }

  const handleCrop = () => {
    croppersRef.current.getCroppedFiles().then((croppedFiles) => {
      croppedFiles.original = selectedAssetUrl;
      onChange(croppedFiles);
      setIsCropping(false);
      setModalOpen(false);
    });
  }

  const handleSelect = () => {
    onChange(selectedAssetUrl);
    setModalOpen(false);
  }

  const actions = [
    <div key="crop-container">
      {(cropEnabled && !isCropping && isImage(selectedAssetUrl)) && (
        <Button icon="crop" disabled={!selectedAsset} onClick={handleOpenCropTool}>Crop image</Button>
      )}
    </div>,
    <div key="select-container" className={c.footerButtons}>
      {isCropping ? (
        <>
          <Button onClick={handleBack}>Back</Button>
          <Button variant="primary" disabled={!selectedAsset} onClick={handleCrop}>Crop</Button>
        </>
      ) : (
        <>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="primary" disabled={!selectedAsset} onClick={handleSelect}>Select</Button>
        </>
      )}
    </div>
  ];

  let previewAssets;

  // Only preview the cropped images - not original
  if (inputValue !== null && typeof inputValue === 'object') {
    previewAssets = Object.entries(inputValue).filter(([key]) => key !== 'original');
  }

  return (
    <React.Fragment>
      <FilePicker
        selectedAsset={value}
        previewAssets={previewAssets}
        onUpdateAsset={v => {
          // Allow manual editing to change to single image
          onChange(v ? v : null);
        }}
        onOpenAssetBrowser={() => { setIsCropping(false); setModalOpen(true) }}
        options={options}
      />
      {
        modalOpen &&
        <Modal
          onClose={() => setModalOpen(false)}
          modalStyle="medium"
          actions={actions}
        >
        {
          !isCropping &&
          <FileBrowser
            selectedAsset={selectedAsset}
            selectedAssetUrl={selectedAssetUrl}
            setSelectedAsset={setSelectedAsset}
            path={path}
            setPath={setPath}
          />
        }
        {
          !!isCropping && !!selectedAssetUrl &&
          <ImageCropperManager ref={croppersRef} path={path} imageUrl={selectedAssetUrl} outputType={options.outputType} croppers={croppers} />
        }
        </Modal>
      }
    </React.Fragment>
  );
};

const useStyles = createUseStyles({
  // The dialog's main buttons, side by side.
  footerButtons: { display: 'flex', alignItems: 'center', gap: 8 }
});

export default Container;
